import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faUser, faTrash, faHome } from "@fortawesome/free-solid-svg-icons";
import AdminLayout from "./AdminLayout";
import "./admin.css";

function adminFetch(path, opts = {}) {
  const token = localStorage.getItem("adminToken");
  return fetch(path, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
}

function KycBadge({ status }) {
  return <span className={`adm-badge ${status || "not_submitted"}`}>{status?.replace("_", " ") || "not submitted"}</span>;
}

function UserDetailPanel({ userId, onClose, onRefresh, canDeleteUser }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`/api/admin/users/${userId}`);
      const d   = await res.json();
      if (res.ok) setData(d);
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => {
    (async () => {
      await loadDetail();
    })();
  }, [loadDetail]);

  const handleDeleteProperty = async (propertyId, label) => {
    if (!window.confirm(`Delete property "${label || `#${propertyId}`}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = await adminFetch(`/api/admin/properties/${propertyId}`, { method: "DELETE" });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || "Failed to delete property.");
      await loadDetail();
      onRefresh();
    } catch (err) {
      alert(err.message || "Failed to delete property.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!canDeleteUser || !data?.id) return;
    const fullName = [data.firstname, data.lastname].filter(Boolean).join(" ").trim();
    if (!window.confirm(`Delete user "${fullName || `#${data.id}`}" and all their properties? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = await adminFetch(`/api/admin/users/${data.id}`, { method: "DELETE" });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || "Failed to delete user.");
      onRefresh();
      onClose();
    } catch (err) {
      alert(err.message || "Failed to delete user.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="adm-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-panel">
        <div className="adm-panel-header">
          <h4>User Detail</h4>
          <button className="adm-panel-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="adm-panel-body">
          {loading && <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>}
          {data && !loading && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: "1rem" }}>
                <div style={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontWeight: 700, fontSize: "1rem",
                }}>
                  {data.firstname?.[0]}{data.lastname?.[0]}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: "var(--adm-navy)" }}>{data.firstname} {data.lastname}</div>
                  <div style={{ fontSize: ".8rem", color: "var(--adm-muted)" }}>{data.email}</div>
                </div>
              </div>

              <div className="adm-kv-row"><span className="adm-kv-label">Account Type</span><span className="adm-kv-value" style={{ textTransform: "capitalize" }}>{data.role}</span></div>
              <div className="adm-kv-row"><span className="adm-kv-label">KYC Status</span><span className="adm-kv-value"><KycBadge status={data.kyc_status} /></span></div>
              {data.kyc_verified_at && <div className="adm-kv-row"><span className="adm-kv-label">KYC Verified</span><span className="adm-kv-value">{new Date(data.kyc_verified_at).toLocaleDateString()}</span></div>}
              <div className="adm-kv-row"><span className="adm-kv-label">Phone</span><span className="adm-kv-value">{data.phnumber ? `+${data.countrycode || ""} ${data.phnumber}` : "—"}</span></div>
              <div className="adm-kv-row"><span className="adm-kv-label">Gender</span><span className="adm-kv-value" style={{ textTransform: "capitalize" }}>{data.gender || "—"}</span></div>
              <div className="adm-kv-row"><span className="adm-kv-label">User ID</span><span className="adm-kv-value" style={{ fontSize: ".78rem", color: "var(--adm-muted)" }}>{data.id}</span></div>

              {data.kyc_history && data.kyc_history.length > 0 && (
                <>
                  <hr style={{ margin: "1rem 0", border: "none", borderTop: "1px solid var(--adm-border)" }} />
                  <p style={{ fontSize: ".8rem", fontWeight: 700, color: "var(--adm-navy)", marginBottom: ".5rem" }}>KYC HISTORY</p>
                  {data.kyc_history.map((k) => (
                    <div key={k.id} style={{
                      padding: ".5rem .75rem", marginBottom: ".4rem",
                      borderRadius: 8, background: "#f8fafc",
                      border: "1px solid var(--adm-border)", fontSize: ".82rem",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: ".2rem" }}>
                        <span style={{ textTransform: "capitalize" }}>{k.document_type?.replace("_", " ")}</span>
                        <KycBadge status={k.status} />
                      </div>
                      <div style={{ color: "var(--adm-muted)", fontSize: ".76rem" }}>
                        Submitted: {k.submitted_at ? new Date(k.submitted_at).toLocaleDateString() : "—"}
                        {k.reviewed_at && ` · Reviewed: ${new Date(k.reviewed_at).toLocaleDateString()}`}
                      </div>
                      {k.rejection_reason && (
                        <div style={{ color: "var(--adm-danger)", fontSize: ".76rem", marginTop: ".2rem" }}>
                          {k.rejection_reason}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}

              <hr style={{ margin: "1rem 0", border: "none", borderTop: "1px solid var(--adm-border)" }} />
              <p style={{ fontSize: ".8rem", fontWeight: 700, color: "var(--adm-navy)", marginBottom: ".5rem" }}>PROPERTIES</p>
              {!data.properties || data.properties.length === 0 ? (
                <div style={{ fontSize: ".82rem", color: "var(--adm-muted)" }}>No properties.</div>
              ) : (
                <div style={{ display: "grid", gap: ".45rem" }}>
                  {data.properties.map((p) => (
                    <div key={p.id} style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: ".75rem",
                      border: "1px solid var(--adm-border)",
                      borderRadius: 8,
                      background: "#f8fafc",
                      padding: ".55rem .65rem",
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".4rem", fontWeight: 600, color: "var(--adm-navy)" }}>
                          <FontAwesomeIcon icon={faHome} style={{ color: "var(--adm-muted)" }} />
                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.property_label || `Property #${p.id}`}</span>
                        </div>
                        <div style={{ fontSize: ".76rem", color: "var(--adm-muted)" }}>
                          {p.address || "Location not set"}
                        </div>
                      </div>
                      <button
                        className="adm-btn danger sm"
                        onClick={() => handleDeleteProperty(p.id, p.property_label)}
                        disabled={busy}
                      >
                        <FontAwesomeIcon icon={faTrash} /> Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {canDeleteUser && (
                <>
                  <hr style={{ margin: "1rem 0", border: "none", borderTop: "1px solid var(--adm-border)" }} />
                  <button className="adm-btn danger" onClick={handleDeleteUser} disabled={busy}>
                    <FontAwesomeIcon icon={faTrash} /> Delete User & Properties
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [rows,     setRows]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [kycFilter,setKycFilter]= useState("");
  const [roleFilter,setRoleFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const myRole = localStorage.getItem("adminRole") || "";
  const canDeleteUser = myRole === "super_admin";

  const load = useCallback(async (pg, q, kyc, role) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 20 });
      if (q)   params.set("search",     q);
      if (kyc) params.set("kyc_status", kyc);
      if (role) params.set("role",      role);
      const res  = await adminFetch(`/api/admin/users?${params}`);
      const data = await res.json();
      if (res.ok) { setRows(data.users || []); setTotal(data.total || 0); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page, search, kycFilter, roleFilter); }, [page, search, kycFilter, roleFilter, load]);

  const totalPages = Math.ceil(total / 20) || 1;

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };
  const handleKyc    = (e) => { setKycFilter(e.target.value); setPage(1); };
  const handleRole   = (e) => { setRoleFilter(e.target.value); setPage(1); };

  const handleDeleteUserFromRow = async (e, user) => {
    e.stopPropagation();
    if (!canDeleteUser) return;
    const name = [user.firstname, user.lastname].filter(Boolean).join(" ").trim();
    if (!window.confirm(`Delete user "${name || `#${user.id}`}" and all their properties? This cannot be undone.`)) return;

    setDeletingUserId(user.id);
    try {
      const res = await adminFetch(`/api/admin/users/${user.id}`, { method: "DELETE" });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || "Failed to delete user.");

      if (selected === user.id) setSelected(null);
      await load(page, search, kycFilter, roleFilter);
    } catch (err) {
      alert(err.message || "Failed to delete user.");
    } finally {
      setDeletingUserId(null);
    }
  };

  return (
    <AdminLayout pageTitle="Users">
      <div className="adm-toolbar">
        <div className="adm-search">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={handleSearch}
          />
        </div>
        <select className="adm-select" value={kycFilter} onChange={handleKyc}>
          <option value="">All KYC statuses</option>
          <option value="not_submitted">Not submitted</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="resubmit">Resubmit</option>
        </select>
        <select className="adm-select" value={roleFilter} onChange={handleRole}>
          <option value="">All roles</option>
          <option value="owner">Owner</option>
          <option value="renter">Renter</option>
        </select>
      </div>

      <div className="adm-card">
        <div className="adm-card-header">
          <h3>Users</h3>
          <span style={{ fontSize: ".8rem", color: "var(--adm-muted)" }}>{total} total</span>
        </div>

        {loading && <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>}

        {!loading && rows.length === 0 && (
          <div className="adm-empty">
            <FontAwesomeIcon icon={faUser} />
            <p>No users found.</p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>KYC Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id} className="clickable" onClick={() => setSelected(u.id)}>
                      <td>{u.firstname} {u.lastname}</td>
                      <td style={{ fontSize: ".82rem", color: "var(--adm-muted)" }}>{u.email}</td>
                      <td style={{ textTransform: "capitalize" }}>{u.role}</td>
                      <td><KycBadge status={u.kyc_status} /></td>
                      <td>
                        <div style={{ display: "flex", gap: ".4rem", justifyContent: "flex-end" }}>
                          <button
                            className="adm-btn ghost sm"
                            onClick={(e) => { e.stopPropagation(); setSelected(u.id); }}
                          >
                            View
                          </button>
                          {canDeleteUser && (
                            <button
                              className="adm-btn danger sm"
                              onClick={(e) => handleDeleteUserFromRow(e, u)}
                              disabled={deletingUserId === u.id}
                            >
                              <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="adm-pagination">
              <span>Page {page} of {totalPages}</span>
              <div className="adm-pag-btns">
                <button className="adm-pag-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                <button className="adm-pag-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
              </div>
            </div>
          </>
        )}
      </div>

      {selected && (
        <UserDetailPanel
          userId={selected}
          onClose={() => setSelected(null)}
          onRefresh={() => load(page, search, kycFilter, roleFilter)}
          canDeleteUser={canDeleteUser}
        />
      )}
    </AdminLayout>
  );
}
